"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.transpile = exports.checkForUndefinedVariablesAndTransformAssignmentsToPropagateBackNewValue = exports.transformImportDeclarations = exports.transformSingleImportDeclaration = void 0;
const walkers_1 = require("../utils/walkers");
const astring_1 = require("astring");
const source_map_1 = require("source-map");
const errors_1 = require("../errors/errors");
const moduleLoader_1 = require("../modules/moduleLoader");
const create = require("../utils/astCreator");
const uniqueIds_1 = require("../utils/uniqueIds");
const constants_1 = require("../constants");
/**
 * This whole transpiler includes many many many many hacks to get stuff working.
 * Order in which certain functions are called matter as well.
 * There should be an explanation on it coming up soon.
 */
const globalIdNames = [
    'native',
    'callIfFuncAndRightArgs',
    'boolOrErr',
    'wrap',
    'unaryOp',
    'binaryOp',
    'throwIfTimeout',
    'setProp',
    'getProp',
    'lastStatementResult',
    'globals'
];
function prefixModule(program) {
    let moduleCounter = 0;
    let prefix = '';
    for (const node of program.body) {
        if (node.type !== 'ImportDeclaration') {
            break;
        }
        const moduleText = moduleLoader_1.loadModuleText(node.source.value);
        prefix += `const __MODULE_${moduleCounter}__ = (${moduleText.trim()})(${constants_1.MODULE_PARAMS_ID});\n`;
        moduleCounter++;
    }
    return prefix;
}
function transformSingleImportDeclaration(moduleCounter, node) {
    const result = [];
    const tempNamespace = `__MODULE_${moduleCounter}__`;
    // const yyy = __MODULE_xxx__.yyy;
    const neededSymbols = node.specifiers.map(specifier => specifier.local.name);
    for (const symbol of neededSymbols) {
        result.push(create.constantDeclaration(symbol, create.memberExpression(create.identifier(tempNamespace), symbol)));
    }
    return result;
}
exports.transformSingleImportDeclaration = transformSingleImportDeclaration;
function transformImportDeclarations(program) {
    const imports = [];
    let result = [];
    let moduleCounter = 0;
    while (program.body.length > 0 && program.body[0].type === 'ImportDeclaration') {
        imports.unshift(program.body.shift());
    }
    for (const node of imports) {
        result = transformSingleImportDeclaration(moduleCounter, node).concat(result);
        moduleCounter++;
    }
    program.body = result.concat(program.body);
}
exports.transformImportDeclarations = transformImportDeclarations;
function createStatementAstToStoreBackCurrentlyDeclaredGlobal(name, kind, globalIds, usedIdentifiers) {
    const paramName = create.identifier(uniqueIds_1.getUniqueId(usedIdentifiers));
    const variableIdentifier = create.identifier(name);
    const properties = [
        create.property('kind', create.literal(kind)),
        create.property('getValue', create.blockArrowFunction([], [create.returnStatement(variableIdentifier)]))
    ];
    if (kind === 'let') {
        properties.push(create.property('assignNewValue', create.functionExpression([paramName], [create.returnStatement(create.assignmentExpression(variableIdentifier, paramName))])));
    }
    return create.expressionStatement(create.callExpression(create.memberExpression(create.memberExpression(globalIds.globals, 'variables'), 'set'), [create.literal(name), create.objectExpression(properties)]));
}
function wrapWithPreviouslyDeclaredGlobals(statements, nativeStorage, globalIds) {
    let currentVariableScope = nativeStorage.globals.previousScope;
    let timesToGoUp = 1;
    let wrapped = create.blockStatement(statements);
    while (currentVariableScope !== null) {
        const initialisingStatements = [];
        currentVariableScope.variables.forEach(({ kind }, name) => {
            let unwrappedValueAst = globalIds.globals;
            for (let i = 0; i < timesToGoUp; i += 1) {
                unwrappedValueAst = create.memberExpression(unwrappedValueAst, 'previousScope');
            }
            unwrappedValueAst = create.callExpression(create.memberExpression(create.callExpression(create.memberExpression(create.memberExpression(unwrappedValueAst, 'variables'), 'get'), [create.literal(name)]), 'getValue'), []);
            initialisingStatements.push(create.declaration(name, kind, unwrappedValueAst));
        });
        timesToGoUp += 1;
        currentVariableScope = currentVariableScope.previousScope;
        wrapped = create.blockStatement([...initialisingStatements, wrapped]);
    }
    return wrapped;
}
function createStatementsToStoreCurrentlyDeclaredGlobals(program, globalIds, usedIdentifiers) {
    return program.body
        .filter(statement => statement.type === 'VariableDeclaration')
        .map(({ declarations: { 0: { id } }, kind }) => createStatementAstToStoreBackCurrentlyDeclaredGlobal(id.name, kind, globalIds, usedIdentifiers));
}
function generateFunctionsToStringMap(program) {
    const map = new Map();
    walkers_1.simple(program, {
        ArrowFunctionExpression(node) {
            map.set(node, astring_1.generate(node));
        },
        FunctionDeclaration(node) {
            map.set(node, astring_1.generate(node));
        }
    });
    return map;
}
function transformFunctionDeclarationsToArrowFunctions(program, functionsToStringMap) {
    walkers_1.simple(program, {
        FunctionDeclaration(node) {
            const { id, params, body } = node;
            node.type = 'VariableDeclaration';
            node = node;
            const asArrowFunction = create.blockArrowFunction(params, body);
            functionsToStringMap.set(asArrowFunction, functionsToStringMap.get(node));
            node.declarations = [
                {
                    type: 'VariableDeclarator',
                    id: id,
                    init: asArrowFunction
                }
            ];
            node.kind = 'const';
        }
    });
}
/**
 * Transforms all arrow functions
 * (arg1, arg2, ...) => { statement1; statement2; return statement3; }
 *
 * to
 *
 * <NATIVE STORAGE>.operators.wrap((arg1, arg2, ...) => {
 *   statement1;statement2;return statement3;
 * })
 *
 * to allow for iterative processes to take place
 */
function wrapArrowFunctionsToAllowNormalCallsAndNiceToString(program, functionsToStringMap, globalIds) {
    walkers_1.simple(program, {
        ArrowFunctionExpression(node) {
            // If it's undefined then we're dealing with a thunk
            if (functionsToStringMap.get(node) !== undefined) {
                create.mutateToCallExpression(node, globalIds.wrap, [
                    Object.assign({}, node),
                    create.literal(functionsToStringMap.get(node)),
                    globalIds.native
                ]);
            }
        }
    });
}
/**
 * Transforms all return statements (including expression arrow functions) to return an intermediate value
 * return nonFnCall + 1;
 *  =>
 * return {isTail: false, value: nonFnCall + 1};
 *
 * return fnCall(arg1, arg2);
 * => return {isTail: true, function: fnCall, arguments: [arg1, arg2]}
 *
 * conditional and logical expressions will be recursively looped through as well
 */
function transformReturnStatementsToAllowProperTailCalls(program) {
    function transformLogicalExpression(expression) {
        switch (expression.type) {
            case 'LogicalExpression':
                return create.logicalExpression(expression.operator, expression.left, transformLogicalExpression(expression.right), expression.loc);
            case 'ConditionalExpression':
                return create.conditionalExpression(expression.test, transformLogicalExpression(expression.consequent), transformLogicalExpression(expression.alternate), expression.loc);
            case 'CallExpression':
                expression = expression;
                const { line, column } = expression.loc.start;
                const functionName = expression.callee.type === 'Identifier' ? expression.callee.name : '<anonymous>';
                const args = expression.arguments;
                return create.objectExpression([
                    create.property('isTail', create.literal(true)),
                    create.property('function', expression.callee),
                    create.property('functionName', create.literal(functionName)),
                    create.property('arguments', create.arrayExpression(args)),
                    create.property('line', create.literal(line)),
                    create.property('column', create.literal(column))
                ]);
            default:
                return create.objectExpression([
                    create.property('isTail', create.literal(false)),
                    create.property('value', expression)
                ]);
        }
    }
    walkers_1.simple(program, {
        ReturnStatement(node) {
            node.argument = transformLogicalExpression(node.argument);
        },
        ArrowFunctionExpression(node) {
            if (node.expression) {
                node.body = transformLogicalExpression(node.body);
            }
        }
    });
}
function transformCallExpressionsToCheckIfFunction(program, globalIds) {
    walkers_1.simple(program, {
        CallExpression(node) {
            const { line, column } = node.loc.start;
            const args = node.arguments;
            node.arguments = [
                node.callee,
                create.literal(line),
                create.literal(column),
                ...args
            ];
            node.callee = globalIds.callIfFuncAndRightArgs;
        }
    });
}
function checkForUndefinedVariablesAndTransformAssignmentsToPropagateBackNewValue(program, skipErrors = false, nativeStorage, globalIds) {
    const globalEnvironment = nativeStorage.globals;
    const previousVariablesToAst = new Map();
    let variableScope = globalEnvironment;
    let variableScopeId = globalIds.globals;
    while (variableScope !== null) {
        for (const [name, { kind }] of variableScope.variables) {
            if (!previousVariablesToAst.has(name)) {
                previousVariablesToAst.set(name, {
                    isConstant: kind === 'const',
                    variableLocationId: create.callExpression(create.memberExpression(create.memberExpression(variableScopeId, 'variables'), 'get'), [create.literal(name)])
                });
            }
        }
        variableScope = variableScope.previousScope;
        variableScopeId = create.memberExpression(variableScopeId, 'previousScope');
    }
    for (const node of program.body) {
        if (node.type !== 'ImportDeclaration') {
            break;
        }
        const symbols = node.specifiers.map(specifier => specifier.local.name);
        for (const symbol of symbols) {
            previousVariablesToAst.set(symbol, {
                isConstant: true,
                variableLocationId: create.literal(-1)
            });
        }
    }
    const identifiersIntroducedByNode = new Map();
    function processBlock(node) {
        const identifiers = new Set();
        for (const statement of node.body) {
            if (statement.type === 'VariableDeclaration') {
                identifiers.add(statement.declarations[0].id.name);
            }
            else if (statement.type === 'FunctionDeclaration') {
                identifiers.add(statement.id.name);
            }
        }
        identifiersIntroducedByNode.set(node, identifiers);
    }
    function processFunction(node, ancestors) {
        identifiersIntroducedByNode.set(node, new Set(node.params.map(id => id.name)));
    }
    const identifiersToAncestors = new Map();
    walkers_1.ancestor(program, {
        Program: processBlock,
        BlockStatement: processBlock,
        FunctionDeclaration: processFunction,
        ArrowFunctionExpression: processFunction,
        ForStatement(forStatement, ancestors) {
            const init = forStatement.init;
            if (init.type === 'VariableDeclaration') {
                identifiersIntroducedByNode.set(forStatement, new Set([init.declarations[0].id.name]));
            }
        },
        Identifier(identifier, ancestors) {
            identifiersToAncestors.set(identifier, [...ancestors]);
        },
        Pattern(node, ancestors) {
            if (node.type === 'Identifier') {
                identifiersToAncestors.set(node, [...ancestors]);
            }
            else if (node.type === 'MemberExpression') {
                if (node.object.type === 'Identifier') {
                    identifiersToAncestors.set(node.object, [...ancestors]);
                }
            }
        }
    });
    const nativeInternalNames = new Set(Object.values(globalIds).map(({ name }) => name));
    for (const [identifier, ancestors] of identifiersToAncestors) {
        const name = identifier.name;
        const isCurrentlyDeclared = ancestors.some(a => { var _a; return (_a = identifiersIntroducedByNode.get(a)) === null || _a === void 0 ? void 0 : _a.has(name); });
        if (!isCurrentlyDeclared) {
            if (previousVariablesToAst.has(name)) {
                const lastAncestor = ancestors[ancestors.length - 2];
                const { isConstant, variableLocationId } = previousVariablesToAst.get(name);
                if (lastAncestor.type === 'AssignmentExpression' && lastAncestor.left === identifier) {
                    // if this is an assignment expression, we want to propagate back the change
                    if (isConstant) {
                        throw new errors_1.ConstAssignment(identifier, name);
                    }
                    else {
                        lastAncestor.right = create.callExpression(create.memberExpression(variableLocationId, 'assignNewValue'), [lastAncestor.right]);
                    }
                }
                else {
                    /**
                     * if this is not the left side of an assignment expression, we need to replace this with
                     * the actual value stored in the correct scope, as calling functions defined in a
                     * previous block might have side effects that change the variable.
                     * e.g.
                     * // first eval:
                     * let counter = 0;
                     * function f() {
                     *   counter = counter + 1;
                     * }
                     * becomes
                     * let counter = 0;
                     * function f() {
                     *   counter = counter + 1;
                     * }
                     * variables.set("counter", getValue: ()=>counter, assignNewValue: newValue => counter = newValue);
                     * // second eval (wrong):
                     * f(); counter;
                     * becomes
                     * let counter = variables.get("counter").getValue(); // set back counter;
                     * {
                     *   f(); // this has a side effect of incrementing counter
                     *   return counter; // that does not get propagated here
                     * }
                     * // second eval (fixed):
                     * f(); counter;
                     * becomes
                     * let counter = variables.get("counter").getValue(); // set back counter;
                     * {
                     *   f(); // this has a side effect of incrementing counter
                     *   return (counter = variables.get("counter").getValue()); // we have no choice but to
                     *    // always assume that counter is updated through a side effect and always retrieve its
                     *    // real value
                     * }
                     */
                    if (!isConstant) {
                        // if it is a constant, it will definitely not mutate so above change is not needed
                        const toExpression = identifier;
                        toExpression.type = 'AssignmentExpression';
                        toExpression.operator = '=';
                        toExpression.left = create.identifier(name);
                        toExpression.right = create.callExpression(create.memberExpression(variableLocationId, 'getValue'), []);
                    }
                }
            }
            else if (!nativeInternalNames.has(name)) {
                if (!skipErrors) {
                    throw new errors_1.UndefinedVariable(name, identifier);
                }
            }
        }
    }
}
exports.checkForUndefinedVariablesAndTransformAssignmentsToPropagateBackNewValue = checkForUndefinedVariablesAndTransformAssignmentsToPropagateBackNewValue;
function transformSomeExpressionsToCheckIfBoolean(program, globalIds) {
    function transform(node) {
        const { line, column } = node.loc.start;
        const test = node.type === 'LogicalExpression' ? 'left' : 'test';
        node[test] = create.callExpression(globalIds.boolOrErr, [
            node[test],
            create.literal(line),
            create.literal(column)
        ]);
    }
    walkers_1.simple(program, {
        IfStatement: transform,
        ConditionalExpression: transform,
        LogicalExpression: transform,
        ForStatement: transform,
        WhileStatement: transform
    });
}
function getNativeIds(program, usedIdentifiers) {
    const globalIds = {};
    for (const identifier of globalIdNames) {
        globalIds[identifier] = create.identifier(uniqueIds_1.getUniqueId(usedIdentifiers, identifier));
    }
    return globalIds;
}
/**
 * statement1;
 * statement2;
 * ...
 * const a = 1; //lastStatement example 1 (should give undefined)
 * 1 + 1; //lastStatement example 2 (should give 2)
 * b = fun(5); //lastStatement example 3 (should set b to fun(5))
 * if (true) { true; } else { false; } //lastStatement example 4 (should give true)
 * for (let i = 0; i < 5; i = i + 1) { i; } //lastStatement example 5 (should give 4)
 *
 * We want to preserve the last evaluated statement's result to return back, so
 * for const/let declarations we simply don't change anything, and return undefined
 * at the end.
 *
 * For others, we will convert it into a string, wrap it in an eval, and store
 * the result in a temporary variable. e.g.
 *
 * const tempVar = eval("1+1;");
 * const tempVar = eval("if (true) { true; } else { false; }");
 * etc etc...
 * now at the end of all the appended statements we can do
 * return tempVar;
 */
function splitLastStatementIntoStorageOfResultAndAccessorPair(lastStatement, globalIds) {
    if (lastStatement.type === 'VariableDeclaration') {
        return {
            lastStatementStoredInResult: lastStatement
        };
    }
    const map = new source_map_1.SourceMapGenerator({ file: 'lastline' });
    const lastStatementAsCode = astring_1.generate(lastStatement, { lineEnd: ' ', sourceMap: map, version: 3 });
    const uniqueDeclarationToStoreLastStatementResult = create.expressionStatement(create.assignmentExpression(globalIds.lastStatementResult, create.callExpression(create.identifier('eval'), [
        create.literal(lastStatementAsCode, lastStatement.loc)
    ])));
    return {
        lastStatementStoredInResult: uniqueDeclarationToStoreLastStatementResult,
        evalMap: map.toJSON()
    };
}
function transformUnaryAndBinaryOperationsToFunctionCalls(program, globalIds, chapter) {
    walkers_1.simple(program, {
        BinaryExpression(node) {
            const { line, column } = node.loc.start;
            const { operator, left, right } = node;
            create.mutateToCallExpression(node, globalIds.binaryOp, [
                create.literal(operator),
                create.literal(chapter),
                left,
                right,
                create.literal(line),
                create.literal(column)
            ]);
        },
        UnaryExpression(node) {
            const { line, column } = node.loc.start;
            const { operator, argument } = node;
            create.mutateToCallExpression(node, globalIds.unaryOp, [
                create.literal(operator),
                argument,
                create.literal(line),
                create.literal(column)
            ]);
        }
    });
}
function getComputedProperty(computed, property) {
    return computed ? property : create.literal(property.name);
}
function transformPropertyAssignment(program, globalIds) {
    walkers_1.simple(program, {
        AssignmentExpression(node) {
            if (node.left.type === 'MemberExpression') {
                const { object, property, computed, loc } = node.left;
                const { line, column } = loc.start;
                create.mutateToCallExpression(node, globalIds.setProp, [
                    object,
                    getComputedProperty(computed, property),
                    node.right,
                    create.literal(line),
                    create.literal(column)
                ]);
            }
        }
    });
}
function transformPropertyAccess(program, globalIds) {
    walkers_1.simple(program, {
        MemberExpression(node) {
            const { object, property, computed, loc } = node;
            const { line, column } = loc.start;
            create.mutateToCallExpression(node, globalIds.getProp, [
                object,
                getComputedProperty(computed, property),
                create.literal(line),
                create.literal(column)
            ]);
        }
    });
}
function addInfiniteLoopProtection(program, globalIds, usedIdentifiers) {
    const getTimeAst = () => create.callExpression(create.identifier('get_time'), []);
    function instrumentLoops(node) {
        const newStatements = [];
        for (const statement of node.body) {
            if (statement.type === 'ForStatement' || statement.type === 'WhileStatement') {
                const startTimeConst = uniqueIds_1.getUniqueId(usedIdentifiers, 'startTime');
                newStatements.push(create.constantDeclaration(startTimeConst, getTimeAst()));
                if (statement.body.type === 'BlockStatement') {
                    const { line, column } = statement.loc.start;
                    statement.body.body.unshift(create.expressionStatement(create.callExpression(globalIds.throwIfTimeout, [
                        globalIds.native,
                        create.identifier(startTimeConst),
                        getTimeAst(),
                        create.literal(line),
                        create.literal(column)
                    ])));
                }
            }
            newStatements.push(statement);
        }
        node.body = newStatements;
    }
    walkers_1.simple(program, {
        Program: instrumentLoops,
        BlockStatement: instrumentLoops
    });
}
function transpile(program, context, skipUndefinedVariableErrors = false) {
    const usedIdentifiers = new Set([
        ...uniqueIds_1.getIdentifiersInProgram(program),
        ...uniqueIds_1.getIdentifiersInNativeStorage(context.nativeStorage)
    ]);
    const globalIds = getNativeIds(program, usedIdentifiers);
    context.nativeStorage.globals = {
        variables: new Map(),
        previousScope: context.nativeStorage.globals
    };
    if (program.body.length === 0) {
        return { transpiled: '' };
    }
    const functionsToStringMap = generateFunctionsToStringMap(program);
    transformReturnStatementsToAllowProperTailCalls(program);
    transformCallExpressionsToCheckIfFunction(program, globalIds);
    transformUnaryAndBinaryOperationsToFunctionCalls(program, globalIds, context.chapter);
    transformSomeExpressionsToCheckIfBoolean(program, globalIds);
    transformPropertyAssignment(program, globalIds);
    transformPropertyAccess(program, globalIds);
    checkForUndefinedVariablesAndTransformAssignmentsToPropagateBackNewValue(program, skipUndefinedVariableErrors, context.nativeStorage, globalIds);
    transformFunctionDeclarationsToArrowFunctions(program, functionsToStringMap);
    wrapArrowFunctionsToAllowNormalCallsAndNiceToString(program, functionsToStringMap, globalIds);
    addInfiniteLoopProtection(program, globalIds, usedIdentifiers);
    const modulePrefix = prefixModule(program);
    transformImportDeclarations(program);
    const statementsToSaveDeclaredGlobals = createStatementsToStoreCurrentlyDeclaredGlobals(program, globalIds, usedIdentifiers);
    const statements = program.body;
    const lastStatement = statements.pop();
    const { lastStatementStoredInResult, evalMap } = splitLastStatementIntoStorageOfResultAndAccessorPair(lastStatement, globalIds);
    const body = [
        wrapWithPreviouslyDeclaredGlobals([...statements, lastStatementStoredInResult, ...statementsToSaveDeclaredGlobals], context.nativeStorage, globalIds),
        create.expressionStatement(globalIds.lastStatementResult)
    ];
    program.body = [...getDeclarationsToAccessTranspilerInternals(globalIds), ...body];
    const map = new source_map_1.SourceMapGenerator({ file: 'source' });
    const transpiled = modulePrefix + astring_1.generate(program, { sourceMap: map });
    const codeMap = map.toJSON();
    return { transpiled, codeMap, evalMap };
}
exports.transpile = transpile;
function getDeclarationsToAccessTranspilerInternals(globalIds) {
    return Object.entries(globalIds).map(([key, { name }]) => {
        let value;
        let kind = 'const';
        if (key === 'native') {
            value = create.identifier(constants_1.NATIVE_STORAGE_ID);
        }
        else if (key === 'lastStatementResult') {
            value = create.primitive(undefined);
            kind = 'let';
        }
        else if (key === 'globals') {
            value = create.memberExpression(globalIds.native, 'globals');
        }
        else {
            value = create.callExpression(create.memberExpression(create.memberExpression(globalIds.native, 'operators'), 'get'), [create.literal(key)]);
        }
        return create.declaration(name, kind, value);
    });
}
//# sourceMappingURL=transpiler.js.map